import * as React from "react";
import { Typography, Button, DialogTitle, Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { apiBulkUpdateGraderWorkitems } from "../../../utils/api";
import { SnackbarContext } from "../../../context/AlertsContext";

type Props = {
    selected: any[];
    open: boolean;
    handleClose: () => void;
    handleAfterRevert: () => void;
    isQualityAssessment?: boolean;
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    }
}));

const StyledButton = styled(Button)({
    borderRadius: "4px",
    textTransform: "none",
    flexGrow: 1,
    maxWidth: '100px',
    maxHeight: '40px',
    minWidth: '100px',
    minHeight: '40px'
});

export const RevertApproval = ({
    selected,
    open,
    handleClose,
    handleAfterRevert,
    isQualityAssessment,
    ...props
}: Props) => {
    const { openSnackbar } = React.useContext(SnackbarContext);
    const [isReverting, setIsReverting] = React.useState(false);

    const handleRevertClick = async () => {
        if (!selected || selected.length === 0) return;

        // Build the updates array for the API call
        const updates = selected.map((item: any) => ({
            sdCaseId: item.sd_case_id || item.case_id, // Handle both possible field names
            status: "CQA_COMPLETE"
        }));

        setIsReverting(true);

        try {
            await apiBulkUpdateGraderWorkitems(updates);

            openSnackbar(
                `${selected.length} work item${selected.length > 1 ? "s" : ""} successfully reverted to Complete status.`,
                "success"
            );

            handleAfterRevert();
            handleClose();
        } catch (err) {
            console.error("Error reverting work items:", err);
            openSnackbar(
                `Failed to revert work items. Please try again.`,
                "error"
            );
        } finally {
            setIsReverting(false);
        }
    };

    return (
        <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ m: 0, p: 2 }} id="revert-approval-title">
                Revert Approval
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
                disabled={isReverting}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <Typography gutterBottom sx={{ mb: 2 }}>
                    {selected ? selected.length : 0} work item{selected && selected.length !== 1 ? "s" : ""} selected.
                    <br />
                    <br />
                    You are about to revert {selected ? selected.length : 0} approved work item
                    {selected && selected.length !== 1 ? "s" : ""} back to Complete status.
                    This will allow coders to edit them again before resubmission.
                    <br />
                    <br />
                    Are you sure you want to proceed?
                </Typography>
            </DialogContent>
            <DialogActions>
                <StyledButton
                    variant="outlined"
                    onClick={() => handleClose()}
                    disabled={isReverting}
                >
                    Cancel
                </StyledButton>
                <StyledButton
                    variant="contained"
                    disabled={!selected || selected.length === 0 || isReverting}
                    sx={{ ml: 5 }}
                    onClick={handleRevertClick}
                >
                    {isReverting ? "Reverting..." : "Revert"}
                </StyledButton>
            </DialogActions>
        </StyledDialog>
    );
};